<template>
  <div class="step-form">
    <div class="row">
      <div class="col-md-4">
        <label>{{ $t("stock_management.transactions.date") }}</label>
        <DxDateBox
          type="date"
          :value="local.storage_loss_date"
          :display-format="getDxGridDefaultDateFormat"
          :open-on-field-click="true"
          :class="{ 'field-error': this.$v.local.storage_loss_date.$error }"
          @value-changed="onChange('storage_loss_date', $event.value)"
        />
      </div>
      <div class="col-md-4">
        <label>{{ $t("common.legal_entity_name") }}</label>
        <DxSelectBox
          :data-source="legalEntities"
          value-expr="uuid"
          display-expr="name"
          :value="local.legal_entity_uuid"
          :class="{ 'field-error': this.$v.local.legal_entity_uuid.$error }"
          search-enabled
          @value-changed="onLegalEntityChanged"
        />
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-md-4">
        <label>{{ $t("common.warehouse") }}</label>
        <DxSelectBox
          :data-source="warehouses"
          value-expr="uuid"
          display-expr="name"
          :value="local.warehouse_uuid"
          :class="{ 'field-error': this.$v.local.warehouse_uuid.$error }"
          search-enabled
          @value-changed="onWarehouseChanged"
        />
      </div>
      <div class="col-md-4">
        <label>{{ $t("common.product") }}</label>
        <div class="sm-select-input-dialog-btn" @click="productClick" :class="{ 'field-error': this.$v.local.product_uuid.$error }">
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ selectedProductName }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-md-4">
        <label>{{ $t("stock_management.storage_losses.engineer") }}</label>
        <DxSelectBox
          :data-source="persons"
          value-expr="uuid"
          display-expr="name"
          :value="local.engineer_uuid"
          :class="{ 'field-error': this.$v.local.engineer_uuid.$error }"
          search-enabled
          @value-changed="onPersonChanged('engineer_uuid', $event)"
        />
      </div>
      <div class="col-md-4">
        <label>{{ $t("stock_management.storage_losses.warehouse_manager") }}</label>
        <DxSelectBox
          :data-source="persons"
          value-expr="uuid"
          display-expr="name"
          :value="local.warehouse_manager_uuid"
          :class="{ 'field-error': this.$v.local.warehouse_manager_uuid.$error }"
          search-enabled
          @value-changed="onPersonChanged('warehouse_manager_uuid', $event)"
        />
      </div>
    </div>

    <div v-if="isLotManagedProduct" class="row mt-2">
      <div class="col-md-4">
        <label>{{ $t("stock_management.storage_losses.lot_filter") }}</label>
        <DxTagBox
          :data-source="lotOptions"
          value-expr="value"
          display-expr="label"
          :value="local.lots || []"
          :search-enabled="true"
          :show-selection-controls="true"
          :multiline="false"
          @value-changed="onChange('lots', $event.value || [])"
        />
      </div>
      <div class="col-md-4">
        <label>{{ $t("stock_management.storage_losses.flag_filter") }}</label>
        <DxTagBox
          :data-source="labelOptions"
          value-expr="uuid"
          display-expr="name"
          :value="local.label_uuids || []"
          :search-enabled="true"
          :show-selection-controls="true"
          :multiline="false"
          @value-changed="onChange('label_uuids', $event.value || [])"
        />
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-md-8">
        <label>{{ $t("common.notes") }}</label>
        <DxTextArea :value="local.note" :height="90" @value-changed="onChange('note', $event.value)" />
      </div>
    </div>

    <ProductSelector
      :visible="showProductSelectDialog"
      @close="showProductSelectDialog = false"
      @continue="onContinueFromProductSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { DxDateBox } from "devextreme-vue/date-box";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxTextArea } from "devextreme-vue/text-area";
import { DxTagBox } from "devextreme-vue/tag-box";
import { mapActions, mapGetters } from "vuex";
import required from "vuelidate/src/validators/required";
import ProductSelector from "@/components/ProductSelector.vue";
import { format } from "date-fns";

export default {
  name: "StorageLossSettings",
  components: { DxDateBox, DxSelectBox, DxTextArea, DxTagBox, ProductSelector },
  props: {
    formData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      local: { ...this.formData },
      legalEntities: [],
      allWarehouses: [],
      persons: [],
      labelOptions: [],
      lotOptions: [],
      showProductSelectDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      getDxGridDefaultDateFormat: "settings/getDxGridDefaultDateFormat",
    }),
    selectedProductName() {
      return this.local.product_name || " - ";
    },
    isLotManagedProduct() {
      return Number(this.local?.product_lot_management || 0) === 1;
    },
    warehouses() {
      if (!this.local.legal_entity_uuid) {
        return this.allWarehouses;
      }
      return this.allWarehouses.filter((item) => {
        const entityUuid = item?.legal_entity_uuid || item?.department_uuid || null;
        return entityUuid === this.local.legal_entity_uuid;
      });
    },
  },
  watch: {
    formData: {
      deep: true,
      handler(v) {
        this.local = { ...v };
      },
    },
    "local.warehouse_uuid"() {
      this.loadLotOptions();
    },
    "local.product_uuid"() {
      this.loadLotOptions();
    },
  },
  mounted() {
    this.loadBaseData();
  },
  methods: {
    ...mapActions({
      getWarehouses: "smWarehouses/getSmWarehouses",
      getDepartments: "department/getDepartments",
      getPersons: "works-persons/getPersons",
      getLabels: "labelManagement/getLabels",
      getSmStockMovementItemsAvailableSum: "smStocks/getSmStockMovementItemsAvailableSum",
    }),
    async loadBaseData() {
      const [dep, wh, pe] = await Promise.all([
        this.getDepartments({ limit: 999 }),
        this.getWarehouses({ limit: 999 }),
        this.getPersons({ limit: 999 }),
      ]);

      const legalEntities = Array.isArray(dep) ? dep.filter((item) => Number(item.is_legal_entity) === 1 && item.legal_entity) : [];

      this.legalEntities = legalEntities.map((item) => ({
        uuid: item.legal_entity.uuid,
        name: item.legal_entity.name,
      }));
      this.allWarehouses = wh?.data || wh?.data?.data || [];
      this.persons = pe?.data?.data || pe?.data || [];

      const labelsResponse = await this.getLabels({ limit: 999, type_code: "PRODUCT_LOT_CATEGORY" });
      this.labelOptions = labelsResponse?.data?.data || labelsResponse?.data || [];

      this.syncDisplayNamesFromUuids();
      this.loadLotOptions();
    },
    async loadLotOptions() {
      if (!this.local.product_uuid || !this.local.warehouse_uuid) {
        this.lotOptions = [];
        return;
      }

      const response = await this.getSmStockMovementItemsAvailableSum({
        product_uuid: this.local.product_uuid,
        warehouse_uuid: this.local.warehouse_uuid,
        movement_date: this.formatDateOnly(this.local.storage_loss_date) || undefined,
        group_by: ["lot", "label_uuid"],
        enablePagination: 0,
        limit: 99999,
      });

      const rows = response?.data?.data || response?.data || [];
      const uniqueLots = Array.from(new Set(rows.map((item) => item?.lot).filter((lot) => !!lot)));
      this.lotOptions = uniqueLots.map((lot) => ({ value: lot, label: lot }));
    },
    formatDateOnly(value) {
      if (!value) return value;
      if (typeof value === "string") return value;
      return format(value, "yyyy-MM-dd");
    },
    onChange(field, value) {
      this.local = { ...this.local, [field]: value };
      this.$emit("input", { [field]: value });
    },
    syncDisplayNamesFromUuids() {
      const legalEntity = this.legalEntities.find((item) => item.uuid === this.local.legal_entity_uuid) || null;
      const warehouse = this.warehouses.find((item) => item.uuid === this.local.warehouse_uuid) || null;
      const engineer = this.persons.find((item) => item.uuid === this.local.engineer_uuid) || null;
      const manager = this.persons.find((item) => item.uuid === this.local.warehouse_manager_uuid) || null;

      const patch = {
        legal_entity_name: this.local.legal_entity_name || legalEntity?.name || null,
        warehouse_name: this.local.warehouse_name || warehouse?.name || null,
        engineer_name: this.local.engineer_name || engineer?.name || null,
        warehouse_manager_name: this.local.warehouse_manager_name || manager?.name || null,
      };

      this.local = {
        ...this.local,
        ...patch,
      };

      this.$emit("input", patch);
    },
    onLegalEntityChanged(e) {
      const uuid = e?.value || null;
      const entity = this.legalEntities.find((item) => item.uuid === uuid) || null;

      this.local = {
        ...this.local,
        legal_entity_uuid: uuid,
        legal_entity_name: entity?.name || null,
      };

      this.$emit("input", {
        legal_entity_uuid: uuid,
        legal_entity_name: entity?.name || null,
      });
    },
    onWarehouseChanged(e) {
      const uuid = e?.value || null;
      const warehouse = this.warehouses.find((item) => item.uuid === uuid) || null;

      this.local = {
        ...this.local,
        warehouse_uuid: uuid,
        warehouse_name: warehouse?.name || null,
      };

      this.$emit("input", {
        warehouse_uuid: uuid,
        warehouse_name: warehouse?.name || null,
      });
    },
    onPersonChanged(field, e) {
      const uuid = e?.value || null;
      const person = this.persons.find((item) => item.uuid === uuid) || null;
      const nameField = field === "engineer_uuid" ? "engineer_name" : "warehouse_manager_name";

      this.local = {
        ...this.local,
        [field]: uuid,
        [nameField]: person?.name || null,
      };

      this.$emit("input", {
        [field]: uuid,
        [nameField]: person?.name || null,
      });
    },
    productClick() {
      this.showProductSelectDialog = true;
    },
    onContinueFromProductSelector(selectedProducts) {
      const selected = Array.isArray(selectedProducts) && selectedProducts.length ? selectedProducts[0] : null;

      this.local = {
        ...this.local,
        product_uuid: selected?.uuid || null,
        product_name: selected?.name || null,
        product_code: selected?.code || null,
        product_lot_management: selected?.lot_management ?? null,
        lots: Number(selected?.lot_management || 0) === 1 ? this.local.lots || [] : [],
        label_uuids: Number(selected?.lot_management || 0) === 1 ? this.local.label_uuids || [] : [],
      };

      this.$emit("input", {
        product_uuid: selected?.uuid || null,
        product_name: selected?.name || null,
        product_code: selected?.code || null,
        product_lot_management: selected?.lot_management ?? null,
        lots: Number(selected?.lot_management || 0) === 1 ? this.local.lots || [] : [],
        label_uuids: Number(selected?.lot_management || 0) === 1 ? this.local.label_uuids || [] : [],
      });

      this.showProductSelectDialog = false;
    },
    validate() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return [this.$t("fill_out_error")];
      }
      return [];
    },
  },
  validations() {
    return {
      local: {
        storage_loss_date: { required },
        legal_entity_uuid: { required },
        warehouse_uuid: { required },
        product_uuid: { required },
        engineer_uuid: { required },
        warehouse_manager_uuid: { required },
      },
    };
  },
};
</script>
